import type { CRMUser } from '../types';
import type { PurchaseRequisition } from '../types/purchasing';

const AGENT_SUFFIX = /\s+(?:via|by)\s+agent(?:\s*\([^)]*\))?$/i;
const AGENT_DASH = /^agent\s*-\s+/i;

export function isAgentRequesterLabel(name: string | undefined | null): boolean {
  const trimmed = String(name || '').trim();
  return AGENT_SUFFIX.test(trimmed) || AGENT_DASH.test(trimmed);
}

/** Initials from "ML via agent (Hugh)" or "Agent - Hugh". */
export function initialsFromAgentLabel(name: string | undefined | null): string | undefined {
  const trimmed = String(name || '').trim();
  const dash = /^agent\s*-\s+(.+)$/i.exec(trimmed);
  if (dash) {
    const parts = dash[1].trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return undefined;
    if (parts.length === 1) return (parts[0][0] || '?').toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  const via = /^([A-Za-z]{1,3})\s+(?:via|by)\s+agent\b/i.exec(trimmed);
  if (via) return via[1].toUpperCase();
  return undefined;
}

function agentLabelBase(displayName: string): string {
  return String(displayName || '')
    .trim()
    .replace(AGENT_DASH, '')
    .replace(AGENT_SUFFIX, '')
    .trim();
}

function initialsFromPersonName(displayName: string): string {
  const base = agentLabelBase(displayName);
  if (!base) return '??';
  const parts = base.split(/\s+/).filter(Boolean);
  const alreadyInitials = parts.length === 1 && /^[A-Za-z]{1,3}$/.test(parts[0]);
  if (alreadyInitials) return parts[0].toUpperCase();
  return parts.map(part => part[0] || '').join('').toUpperCase().slice(0, 3) || '??';
}

/** "hugh" / "agent:hugh" → "Hugh". */
export function agentGivenName(raw: string | undefined | null): string | undefined {
  const id = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/^agent:/, '');
  if (!id || !/^[a-z][a-z0-9_-]{0,40}$/.test(id)) return undefined;
  return id.charAt(0).toUpperCase() + id.slice(1);
}

/** "Agent - Hugh" */
export function agentDashLabel(givenName: string): string {
  return `Agent - ${givenName}`;
}

type AgentCreatedRow = Pick<PurchaseRequisition, 'createdViaAgent' | 'source' | 'createdByAgentId' | 'requesterName'>;

export function isAgentCreatedPurchase(row: Pick<PurchaseRequisition, 'createdViaAgent' | 'source' | 'requesterName'>): boolean {
  return (
    row.createdViaAgent === true
    || row.source === 'crew_slack_agent'
    || isAgentRequesterLabel(row.requesterName)
  );
}

/** Agent that filed the requisition. Crew Slack purchases are Hugh's. */
export function creatingAgentGivenName(row: AgentCreatedRow): string | undefined {
  const fromId = agentGivenName(row.createdByAgentId);
  if (fromId) return fromId;
  if (row.source === 'crew_slack_agent' || row.createdViaAgent === true) return 'Hugh';
  return undefined;
}

/** "Matthew Lynch" → "ML via agent (Hugh)" when the filing agent is known. */
export function agentRequesterLabelFromName(displayName: string, agentGiven?: string): string {
  const initials = initialsFromPersonName(displayName);
  if (agentGiven?.trim()) return `${initials} via agent (${agentGiven.trim()})`;
  return `${initials} via Agent`;
}

export function purchaseRequesterDisplayName(
  row: Pick<PurchaseRequisition, 'requesterId' | 'requesterName' | 'createdViaAgent' | 'source' | 'createdByAgentId'>,
  crmUsers: Array<Pick<CRMUser, 'uid' | 'displayName'>>,
): string {
  const stored = row.requesterName?.trim() || '';
  if (!isAgentCreatedPurchase(row)) {
    const id = row.requesterId?.trim();
    if (id) {
      const match = crmUsers.find(u => u.uid === id);
      const fromDirectory = match?.displayName?.trim();
      if (fromDirectory) return fromDirectory;
    }
    return stored || 'Unknown';
  }
  const agent = creatingAgentGivenName(row);
  if (isAgentRequesterLabel(stored)) return agentRequesterLabelFromName(stored, agent);
  const id = row.requesterId?.trim();
  const directory = id ? crmUsers.find(user => user.uid === id)?.displayName?.trim() : '';
  return agentRequesterLabelFromName(directory || stored || '??', agent);
}

/** Header "Requested by" control. Agent-filed rows name the agent, not the person. */
export function purchaseRequestedByHeaderLabel(
  row: Pick<PurchaseRequisition, 'requesterId' | 'requesterName' | 'createdViaAgent' | 'source' | 'createdByAgentId'>,
  crmUsers: Array<Pick<CRMUser, 'uid' | 'displayName'>>,
): string {
  if (isAgentCreatedPurchase(row)) {
    const agent = creatingAgentGivenName(row);
    if (agent) return agentDashLabel(agent);
  }
  return purchaseRequesterDirectoryName(row.requesterId, crmUsers, row.requesterName);
}

export function purchaseRequesterOptionLabel(
  user: { uid: string; displayName: string },
  row: Pick<PurchaseRequisition, 'requesterId' | 'requesterName' | 'createdViaAgent' | 'source' | 'createdByAgentId'>,
): string {
  if (user.uid === row.requesterId && isAgentCreatedPurchase(row)) {
    const agent = creatingAgentGivenName(row);
    if (agent) return agentDashLabel(agent);
  }
  return user.displayName;
}

/** "H via Agent" / agent:hugh → "Agent - Hugh". People stay as stored. */
export function purchasePurchaserDisplayName(row: Pick<
  PurchaseRequisition,
  'purchasedById' | 'purchasedByName' | 'createdViaAgent' | 'source' | 'createdByAgentId' | 'requesterName'
>): string | undefined {
  const fromId = agentGivenName(row.purchasedById);
  if (fromId && String(row.purchasedById || '').toLowerCase().includes('agent')) {
    return agentDashLabel(fromId);
  }
  const stored = row.purchasedByName?.trim();
  if (!stored) return fromId ? agentDashLabel(fromId) : undefined;
  if (!isAgentRequesterLabel(stored)) return stored;
  const agent = fromId || creatingAgentGivenName(row);
  return agent ? agentDashLabel(agent) : stored;
}

/**
 * A date-only purchasedAt is stored at noon UTC so the calendar day survives
 * US timezones. That clock (7:00 AM Central) is not when the purchase was
 * recorded, and it can sort before the request. Use the later write instead.
 */
export function isDateOnlyPurchaseStamp(date: Date): boolean {
  if (Number.isNaN(date.getTime())) return false;
  const midnight = date.getUTCHours() === 0 && date.getUTCMinutes() === 0;
  const noon = date.getUTCHours() === 12 && date.getUTCMinutes() === 0;
  return (midnight || noon) && date.getUTCSeconds() === 0 && date.getUTCMilliseconds() === 0;
}

export function purchaseDisplayedPurchasedAt(row: {
  purchasedAt?: Date;
  createdAt?: Date;
  approvedAt?: Date;
  updatedAt?: Date;
}): Date | undefined {
  const purchased = row.purchasedAt;
  if (!purchased || Number.isNaN(purchased.getTime())) return undefined;
  const requested = row.createdAt;
  const earlierThanRequest = !!requested
    && !Number.isNaN(requested.getTime())
    && purchased.getTime() < requested.getTime();
  if (!earlierThanRequest && !isDateOnlyPurchaseStamp(purchased)) return purchased;
  if (!earlierThanRequest) return purchased;
  const updated = row.updatedAt;
  if (updated && !Number.isNaN(updated.getTime()) && requested && updated.getTime() >= requested.getTime()) {
    return updated;
  }
  const approved = row.approvedAt;
  if (approved && !Number.isNaN(approved.getTime()) && requested && approved.getTime() >= requested.getTime()) {
    return approved;
  }
  return requested || purchased;
}

export function purchaseRequesterDirectoryName(
  requesterId: string,
  crmUsers: Array<Pick<CRMUser, 'uid' | 'displayName'>>,
  fallbackName: string,
): string {
  const match = crmUsers.find(u => u.uid === requesterId);
  return match?.displayName?.trim() || fallbackName.trim() || 'Employee';
}
